import { useMemo, useRef, useCallback } from 'react';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import { post, get, del, put } from '../utils/api';
import { useSuccessSnack, useErrorSnack } from '../utils/snackbar';
import i18n from '../i18n';
import { track } from '../utils/track';

export let UPLOADING_COUNTER = 0;

export async function uploadTranscript({
  form,
  onProgress,
  name,
  language,
  transcribeMode,
  youtubeId,
  integrationMeetingUrl,
  assignedTo,
  url,
  urlData,
  folderPrefix,
  outputFormat,
  numSpeakers,
  abortSignal,
  recording = false,
  to,
  from,
}) {
  track('upload_file', {
    name,
    language,
    transcribeMode,
    youtubeId,
    url,
    recording,
  });

  let data = form;
  if (url === 'google_drive' || url === 'onedrive') {
    data = {
      urlData,
    };
  }

  UPLOADING_COUNTER += 1;

  const res = await post('/upload/transcript', data, {
    onUploadProgress: onProgress,
    params: {
      name,
      language,
      transcribe_mode: transcribeMode,
      url,
      youtube_id: youtubeId,
      integration_meeting_url: integrationMeetingUrl,
      assigned_to: assignedTo,
      folder_prefix: folderPrefix,
      output_format: outputFormat,
      num_speakers: numSpeakers,
      to,
      from,
    },
    signal: abortSignal,
  });

  UPLOADING_COUNTER = Math.max(0, UPLOADING_COUNTER - 1);

  return res?.data;
}

async function uploadFile({ form, mimeType, params, onProgress }) {
  UPLOADING_COUNTER += 1;

  track('upload_document', { mimeType });
  const res = await post('/upload/file', form, {
    onUploadProgress: onProgress,
    params,
    headers: {
      'X-Document-Content-Type': mimeType,
    },
  });

  UPLOADING_COUNTER = Math.max(0, UPLOADING_COUNTER - 1);

  return res?.data;
}

export function useFileUploadActions() {
  const {
    mutate: _uploadFile,
    mutateAsync: uploadFileAsync,
    error: _uploadError,
    isError: isUploadError,
    isLoading: isLoadingUpload,
  } = useMutation(uploadFile);

  return {
    uploadFile: _uploadFile,
    uploadFileAsync,
    error: _uploadError,
    isUploadError,
    isLoadingUpload,
  };
}

export function useTranscriptUploadActions() {
  const client = useQueryClient();

  const controller = useRef(new AbortController());
  const {
    mutate: _uploadFile,
    mutateAsync: uploadFileAsync,
    error: _uploadError,
    isError: isUploadError,
    isLoading: isLoadingUpload,
    isSuccess: isSuccesUpload,
    status: uploadStatus,
    reset: resetUpload,
  } = useMutation(
    (data) =>
      uploadTranscript({ ...data, abortSignal: controller.current.signal }),
    {
      onSettled: () => {
        client.invalidateQueries('meetings');
        client.invalidateQueries('organization');
        client.invalidateQueries('folderAssets');
      },
    }
  );

  const cancelUpload = useCallback(() => {
    controller.current.abort();
    resetUpload();
    controller.current = new AbortController();
  }, [resetUpload]);

  const uploadError = useMemo(() => {
    if (_uploadError) {
      const { data, status } = _uploadError?.response || {};
      if (status === 413) {
        return i18n.t('Fel uppstod. Filen är för stor');
      }
      if (status === 500) {
        return i18n.t('Internt serverfel');
      }
      return data;
    }
    return null;
  }, [_uploadError]);

  useSuccessSnack(i18n.t('Fil uppladdad'), isSuccesUpload);
  useErrorSnack(
    i18n.t('Fel uppstod vid uppladning av fil. Var god testa igen'),
    isUploadError
  );

  return {
    uploadTranscript: _uploadFile,
    uploadTranscriptAsync: uploadFileAsync,
    cancelUpload,
    uploadError,
    isUploadError,
    isLoadingUpload,
    uploadStatus,
  };
}

async function getFile(fileId) {
  const res = await get(`/files/${fileId}`);
  return res?.data;
}

export function useFileQuery(fileId) {
  const { data: file } = useQuery(['file', fileId], () => getFile(fileId), {
    enabled: !!fileId,
  });

  return {
    file,
  };
}

async function deleteFile(id) {
  const res = await del(`/files/${id}`);
  return res?.data;
}

async function updateFile(id, data) {
  const res = await put(`/files/${id}`, data);
  return res?.data;
}

export function useFileActions(fileId) {
  const queryClient = useQueryClient();

  const {
    mutate: _deleteFile,
    mutateAsync: deleteFileAsync,
    isLoading,
  } = useMutation(() => deleteFile(fileId), {
    onSettled: () => {
      queryClient.invalidateQueries('folderAssets');
    },
  });

  const { mutate: _updateFile, mutateAsync: updateFileAsync } = useMutation(
    (data) => updateFile(fileId, data),
    {
      onSettled: () => {
        queryClient.invalidateQueries('folderAssets');
      },
    }
  );

  return {
    deleteFile: _deleteFile,
    deleteFileAsync,
    isDeleting: isLoading,
    updateFile: _updateFile,
    updateFileAsync,
  };
}
